import {useHistory, useLocation} from 'react-router-dom';
import queryString from 'query-string';
import {ListSearch} from '../common/ListSearch';
import {ListPagination} from '../common/ListPagination';
import NoItems from '../common/no_items/NoItems';
import Container from '../common/layout/Container';
import Error from '../common/Error';
import {getSortedParams} from '../../helpers';
import Spinner from '../common/Spinner';
import {useCallback, useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {setTitle} from '../../store/header/header.slice';
import Row from './Row';
import permissionsInfo from '../../constants/permissionsInfo';
import url from 'url';
import {deleteCallSheetById, getAllCallSheets} from '../../api/callsheet';
import ConfirmationPopup from '../common/popup/ConfirmationPopup';
import {CallSheetResponseProps} from '../../constants/types';
import {CallsSheetListFilter} from './CallsSheetListFilter';
import {FiltersBodyProps} from './types';
import TableHeaderRow from '../common/TableHeaderRow';
import {noItemsMessages} from '../../constants/constants';
import {useSnackbarHook} from '../../hooks/useSnackbar';
import {useTranslation} from 'react-i18next';
import FilterListIcon from '@mui/icons-material/FilterList';
import IconButton from '@mui/material/IconButton';
import classNames from 'classnames';

const initialQuery = {
  direction: 'asc',
  page: '1',
  per_page: '10',
  simple_search: '',
  sort: ''
};

const entity = {
  fetchUrl: () => '/call-sheets',
  header: '',
  title: '',
  permissions: permissionsInfo.calls.view,
  loadingText: 'Getting call records list',
  defaultSort: 'LAST_NAME,FIRST_NAME',
  popupLabel: (patientName: string) =>
    `Call record for patient "${patientName}" will be removed.`,
  fields: [
    {
      name: 'id',
      label: 'ID',
      sortName: 'CALL_SHEET_ID',
      permission: permissionsInfo.calls.view
    },
    {
      name: 'patientName',
      label: 'Patient',
      sortName: 'LAST_NAME,FIRST_NAME',
      permission: permissionsInfo.calls.view
    },
    {
      name: 'treatmentName',
      label: 'Treatment',
      sortName: 'TREATMENT_NAME',
      permission: permissionsInfo.calls.view
    },
    {
      name: 'appointmentDate',
      label: 'Appointment',
      sortName: 'APPOINTMENT_DATE',
      permission: permissionsInfo.calls.view
    },
    {
      name: 'wantsSooner',
      label: 'Wants Sooner',
      sortName: 'WANTS_SOONER',
      permission: permissionsInfo.calls.view
    },
    {
      name: 'operatoryNumber',
      label: 'Operatory',
      sortName: 'OPERATORY_NUMBER',
      permission: permissionsInfo.calls.view
    },
    {
      name: 'threeDots',
      label: '',
      className: 'narrow-cell',
      permission: [
        ...permissionsInfo.calls.edit,
        ...permissionsInfo.calls.delete
      ]
    }
  ]
};

type DeletionInformationProps = {
  id: number, name: string
};

type LocationParams = {
  search: string,
  pathname: string,
  state?: {
    initialFiltersBody: FiltersBodyProps
  }
}

const CallsSheetList = () => {
  const {t} = useTranslation('callSheets');
  const dispatch = useAppDispatch();
  const {pathname, search, state}: LocationParams = useLocation();
  const history = useHistory();
  const {activeTenant} = useAppSelector(({permissions}) => permissions);
  const {addSuccessToast, addErrorToast} = useSnackbarHook();
  const {initialFiltersBody} = state || {};

  const query = {
    ...initialQuery,
    ...getSortedParams(queryString.parse(search))
  };

  if (!query.sort) {
    query.sort = entity?.defaultSort;
  }

  const [error, setError] = useState('');
  const [callSheetData, setCallSheetData] =
    useState<CallSheetResponseProps | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletePopupShown, setIsDeletePopupShown] = useState(false);
  const [isFilterShown, setIsFilterShown] = useState(false);
  const [filtersBody, setFiltersBody] = useState<FiltersBodyProps>([
    ...(initialFiltersBody ? initialFiltersBody : [])
  ]);
  const [deletionInformation, setDeletionInformation] =
    useState<DeletionInformationProps | null>(null);

  const closeFilters = useCallback(() => {
    setIsFilterShown(false);
  }, []);

  const openFilters = useCallback(() => {
    setIsFilterShown(true);
  }, []);

  useEffect(() => {
    history.replace(url.format({pathname, query}));
    dispatch(setTitle(t('pageTitle')));
  }, []);

  const getCallSheets = async () => {
    setIsLoading(true);
    try {
      const response = await getAllCallSheets(filtersBody, query);
      setCallSheetData(response);
    } catch (error) {
      setError(error?.message || '');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const shouldReturnToFirstPage = query.page !== '1' || query.simple_search;

    if (shouldReturnToFirstPage && callSheetData) {
      history.push(url.format({
        pathname, query: {...query, page: 1, simple_search: ''}}));
    }
  }, [activeTenant]);

  useEffect(() => {
    getCallSheets();

    return () => {
      setError('');
    };
  }, [JSON.stringify(query), JSON.stringify(filtersBody)]);

  const {content = [], pageNumber = 1, totalElements = 0, totalPages = 1} =
  callSheetData || {};
  const contentLength = content?.length;

  const handleDelete = async () => {
    if (!deletionInformation?.id) return;

    try {
      setIsDeleting(true);
      await deleteCallSheetById(deletionInformation.id);
      addSuccessToast(t('toasts.removeCallSheetMessage'));

      if (content.length === 1) {
        query.page = `${Number(query?.page) - 1}`;
        history.push(url.format({pathname, query: query}));
      } else {
        const response = await getAllCallSheets(filtersBody, query);
        setCallSheetData(response);
      }
    } catch (err) {
      addErrorToast(err.message);
    } finally {
      setIsDeletePopupShown(false);
      setIsDeleting(false);
    }
  };

  const getPopupLabel = () => {
    if (!deletionInformation?.name) return '';

    return entity?.popupLabel(deletionInformation?.name);
  };

  if (error) {
    return <Error message={error} />;
  }

  const noItems = !totalElements &&
    !query?.simple_search &&
    !filtersBody.length &&
    !isLoading;
  const noSearchResults = !totalElements &&
    !!query?.simple_search &&
    !filtersBody.length &&
    !isLoading;
  const noFilterResults = !totalElements &&
    !query?.simple_search &&
    !!filtersBody.length &&
    !isLoading;

  const renderRows = () => {
    if (!content) return null;

    return content.map(({id, patient, appointment, currentlyInUse}) => {
      const rowItem = {
        id: id,
        patientName: [patient.lastName, patient.firstName].join(', '),
        treatmentName: appointment?.treatment?.displayName,
        appointmentDate: appointment?.appointmentDate,
        wantsSooner: appointment?.wantsSooner,
        operatoryNumber: appointment?.operatory?.number,
        currentlyInUse
      };

      return <Row key={`call-sheet-row-${id}`}
        rowItem={rowItem}
        confirmDelete={() => {
          setDeletionInformation({id: id, name: rowItem.patientName});
          setIsDeletePopupShown(true);
        }}
      />;
    });
  };

  const tableContent = isLoading && !callSheetData ?
    (<Spinner loadingText={entity?.loadingText}/>) :
    (<Spinner loadingText={entity?.loadingText} isLoading={isLoading}>
      <>
        <table className="table call-sheet-list">
          <thead>
            <TableHeaderRow
              query={query}
              tableData={entity.fields}
              headerRowClassName=""
            />
          </thead>
          <tbody>
            {renderRows()}
          </tbody>
        </table>

        {noSearchResults ?
          (<NoItems message={noItemsMessages.SEARCH}/>) :
         noFilterResults ?
           (<NoItems message={noItemsMessages.FILTER}/>) :
         noItems ?
           (<NoItems message={noItemsMessages.ITEMS}/>) :
           (<ListPagination
             contentLength={contentLength}
             pageNumber={pageNumber}
             totalElements={totalElements}
             totalPages={totalPages}
             query={query}
           />)
        }
      </>
    </Spinner>);

  return (
    <>
      <Container>
        <div className="margin-top-25">
          <div className="list-header flex-end">
            <ListSearch
              entity={entity}
              query={query}
            />
          </div>
          <div className="subheader-and-filter space-between">
            <h2 className="subheader">Patient Records</h2>
            <IconButton
              className={classNames('icon-button blue', {'active': isFilterShown})}
              onClick={openFilters}
            >
              <FilterListIcon className="icon" />
            </IconButton>
          </div>
          {tableContent}
        </div>
      </Container>

      <ConfirmationPopup
        isActionProcessing={isDeleting}
        labels={{body: getPopupLabel()}}
        isOpen={isDeletePopupShown}
        setIsOpen={setIsDeletePopupShown}
        confirmableAction={handleDelete} />

      <CallsSheetListFilter
        isOpen={isFilterShown}
        setFiltersBody={setFiltersBody}
        query={query}
        openFilters={openFilters}
        closeFilters={closeFilters}
      />
    </>
  );
};

export default CallsSheetList;
